import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { StatusPill } from "@/components/shared/StatusPill";
import { Loading } from "@/components/shared/Loading";
import { useCustomers } from "@/api/hooks";
import { money, initials } from "@/lib/format";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { BusinessStage } from "@/components/shared/BusinessStage";
import { CustomerOnboardingDialog } from "@/components/sales/CustomerOnboardingDialog";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";

export default function SalesCustomers() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const { data: customers } = useCustomers();
  if (!customers) return <Loading />;
  const filtered = customers.filter((c) => `${c.name} ${c.id} ${c.industry || ""} ${c.businessStage?.name || ""} ${c.businessStage?.lastStatus || ""}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <>
      <PageHeader title="Customers" subtitle={`${customers.length} accounts · one company, one history`}
        actions={<><div className="relative"><Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" /><Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search customers" className="h-8 pl-8 w-64" data-testid="customer-search" /></div><Button size="sm" onClick={() => setOnboardingOpen(true)} data-testid="customer-onboard"><Plus className="mr-1.5 h-4 w-4" />Onboard customer</Button></>} />
      <div className="flex-1 overflow-y-auto p-6">
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-400">
                <th className="text-left font-semibold px-4 py-2.5">Customer</th>
                <th className="text-left font-semibold px-4 py-2.5">Current stage / last status</th>
                <th className="text-left font-semibold px-4 py-2.5">Finance</th>
                <th className="text-right font-semibold px-4 py-2.5">Credit exposure</th>
                <th className="text-right font-semibold px-4 py-2.5">Overdue</th>
                <th className="text-center font-semibold px-4 py-2.5">Open jobs</th>
                <th className="text-right font-semibold px-4 py-2.5">Health</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((c) => (
                <tr key={c.id} data-testid={`customer-row-${c.id}`} onClick={() => navigate(`/sales/customers/${c.id}`)} className="hover:bg-slate-50 cursor-pointer transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-lg bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center shrink-0">{initials(c.name)}</div>
                      <div><div className="font-semibold text-slate-800">{c.name}</div><div className="text-[11px] text-slate-400 font-mono-num">{c.id} · {c.industry}</div></div>
                    </div>
                  </td>
                  <td className="px-4 py-3 max-w-[280px]"><BusinessStage stage={c.businessStage} /></td>
                  <td className="px-4 py-3"><StatusPill status={c.financeStatus === "Credit" ? "Released" : "Pending"} bucket={c.financeStatus === "Credit" ? "info" : "neutral"} /> <span className="text-[11px] text-slate-400 ml-1">{c.paymentTerms}</span></td>
                  <td className="px-4 py-3 text-right font-mono-num text-slate-700">{c.creditLimit ? `${money(c.exposure)} / ${money(c.creditLimit)}` : "—"}</td>
                  <td className="px-4 py-3 text-right font-mono-num">{c.overdue > 0 ? <span className="text-red-600 font-semibold">{money(c.overdue)}</span> : <span className="text-slate-400">—</span>}</td>
                  <td className="px-4 py-3 text-center font-mono-num text-slate-600">{c.openJobs}</td>
                  <td className="px-4 py-3 text-right"><span className={`font-mono-num font-semibold ${c.health >= 75 ? "text-emerald-600" : c.health >= 60 ? "text-amber-600" : "text-red-600"}`}>{c.health}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <CustomerOnboardingDialog open={onboardingOpen} onOpenChange={setOnboardingOpen} />
    </>
  );
}
